import { useCallback, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'recipease-theme';

function getInitialTheme(): Theme {
	if (typeof window === 'undefined') return 'light';
	const stored = window.localStorage.getItem(STORAGE_KEY);
	if (stored === 'light' || stored === 'dark') return stored;
	return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(theme: Theme): void {
	document.documentElement.setAttribute('data-theme', theme);
}

/**
 * Light/dark theme, persisted to localStorage and reflected on the root as
 * `data-theme`. An inline script in index.html applies the stored value before
 * paint, so this hook only keeps React in sync and handles toggling.
 */
export function useTheme(): { theme: Theme; toggleTheme: () => void } {
	const [theme, setTheme] = useState<Theme>(getInitialTheme);

	useEffect(() => {
		applyTheme(theme);
		try {
			window.localStorage.setItem(STORAGE_KEY, theme);
		} catch {
			/* storage may be unavailable (private mode) — theme still applies for the session */
		}
	}, [theme]);

	const toggleTheme = useCallback(() => {
		setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
	}, []);

	return { theme, toggleTheme };
}
